import ApeHostNotifications from '~/_base/functions/apeHostNotifications';
import { Link, useLocation } from 'react-router-dom';
import { useToast } from '~/_base/context/toastContext';
import ApiCall from '~/_base/functions/apicalls';
import { useTranslation } from 'react-i18next';

export interface RailsMenu {
  key: string;
  label: string;
  name: string;
  tooltip?: string;
  items: RailsOperation[];
}

export interface RailsOperation {
  key?: string;
  method?: string;
  label: string;
  name: string;
  uri: string;
  confirm?: string;
  divider?: boolean;
  is_destructive?: boolean;
  id?: number;
}

/**
 * Hook for setting up the desired onClick props for Buttons/MenuItems
 * from the rails operation convention. As of 21/11/19 this logic is reused
 * across most navigation items (top nav/project nav), data table actions
 * (create, export), and data table item operations (edit, delete, etc.)
 *
 * @param {RailsOperation} operation Server-provided operation
 * @param {function} onClick Fired when the item is clicked
 * @param {function} onSuccess Fired following a successful async operation
 * @param {function} onError Fired following a failed async operation
 * @return {Object} Partial Material UI MenuItem or Button props
 */
export default function useRailsClick(
  operation: RailsOperation,
  onClick?: () => void,
  onSuccess?: (json?: any) => void,
  onError?: (err: string) => void
) {
  operation.method = operation.method?.toLocaleUpperCase();
  const { errorToast } = useToast();
  let location = useLocation();

  let componentProps: any = {
    onClick,
  };
  const { t } = useTranslation();

  if (operation.method === 'REACT') {
    // Navigate to a react-router route
    componentProps.component = Link;
    componentProps.to = operation.uri;
  } else if (operation.method === 'MODAL') {
    // Open a react-router site wide modal
    componentProps.component = Link;
    componentProps.to = operation.uri;
    componentProps.state = { background: location };
  } else if (operation.method === 'GET' && operation.uri) {
    // A GET is always just a standard link which performs some navigation
    // except for one edge case below:

    // Sending a "GET" with no uri seems to be a hack workaround to make
    // buttons display the operation status on android. I.e. clicking
    // "Get PDF" turns the button to "Getting PDF". The JS side does
    // nothing except re-render the button with the new text
    const isAttachment = operation.uri.includes('disposition=attachment');
    componentProps.component = 'a';
    componentProps.href = operation.uri;
    const newTab = isAttachment || operation.uri.indexOf('target=_blank') > -1;
    if (newTab) {
      componentProps.target = '_blank';
    }
  } else if (operation.method === 'DELETE') {
    // Prompt with a native browser confirm and submit a DELETE to the uri
    componentProps.onClick = async () => {
      const warning =
        operation.confirm || t('Are you sure you want to delete this?');
      const confirmDelete = window.confirm(warning);
      if (confirmDelete) {
        if (onClick) onClick();
        try {
          await ApiCall(operation.uri, 'DELETE');
          if (onSuccess) onSuccess();
        } catch (err) {
          if (onError) onError((err as Error).message);
        }
      }
    };
  } else if (operation.method === 'SIGNOUT') {
    // Call the /signout endpoint and redirect to login
    componentProps.onClick = async () => {
      if (onClick) onClick();
      await ApiCall('/signout', 'DELETE');
      try {
        sessionStorage.removeItem('auth_method');
      } catch (e) {
        console.error(e);
      }
      window.location.href = '/';
    };
  } else if (operation.method === 'ALERT') {
    // Display a notification
    componentProps.onClick = () => errorToast(operation.uri);
  } else if (operation.method === 'NOOP') {
    // Legacy (used to open site assistant & liveview)
    componentProps.onClick = () => {
      if (onClick) onClick();
      ApeHostNotifications.notify({
        type: 'NoOperationNavigation',
        uri: operation.uri,
        operation,
      });
    };
  } else if (operation.method === 'PATCH' || operation.method === 'POST') {
    componentProps.onClick = async () => {
      if (onClick) onClick();
      try {
        // Delegating operations to the server means we lose type safety
        // Callers will need to explicitly type cast the result
        const result: any = await ApiCall(
          operation.uri,
          operation.method?.toLocaleUpperCase()
        );
        if (onSuccess) onSuccess(result);
      } catch (err) {
        if (onError) onError((err as Error).message);
      }
    };
  }

  return componentProps;
}
